/**
 * PO search helpers for invoice Match / Allocate.
 * Phase 1: unreconciled + date proximity.
 * Phase 5: exclude already-selected POs, optional value proximity, clearer hints.
 */

/**
 * Id to pass into PO search (`orders/:venueId/search-po`).
 * Prefer Invoice.supplier (VenueSupplier id on the invoice record) — express
 * resolves VenueSupplier → Supplier before filtering Order.supplier.
 * Falls back to mapped supplierId (often the same VenueSupplier id on table rows).
 */
export function getInvoicePoSearchSupplierId(invoice = {}) {
  const venueSupplierId = String(invoice?.supplier || '').trim()
  if (venueSupplierId) return venueSupplierId
  const mappedId = String(invoice?.supplierId || '').trim()
  return mappedId || undefined
}

function toTime(value) {
  if (value == null || value === '') return null
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : null
}

function formatSearchDate(value) {
  const raw = String(value || '')
  const isoDay = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoDay) {
    return `${isoDay[3]}/${isoDay[2]}/${isoDay[1].slice(-2)}`
  }
  const time = toTime(value)
  if (time == null) return ''
  const date = new Date(time)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = String(date.getFullYear()).slice(-2)
  return `${day}/${month}/${year}`
}

function toPenniesOrNull(value) {
  if (value == null || value === '') return null
  const num = Number(value)
  if (!Number.isFinite(num)) return null
  return Math.round(num)
}

function poSearchKey(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
}

const NEAR_INVOICE_DATE_MS = 7 * 24 * 60 * 60 * 1000

function dateDistanceMs(po = {}, invoiceTime) {
  if (invoiceTime == null) return null
  const poDate = toTime(po?.deliveryDate) || toTime(po?.orderedDate)
  if (poDate == null) return null
  return Math.abs(poDate - invoiceTime)
}

function valueDistancePennies(po = {}, invoiceTotalPennies) {
  if (invoiceTotalPennies == null) return null
  const candidate =
    toPenniesOrNull(po?.remainingBalancePennies) ??
    toPenniesOrNull(po?.orderGrossPennies) ??
    toPenniesOrNull(po?.grossPennies)
  if (candidate == null) return null
  return Math.abs(candidate - invoiceTotalPennies)
}

/**
 * Drop POs the user already selected on other Match / Allocate rows.
 * Keeps the current field's own value so clearing/re-selecting still works.
 */
export function filterExcludedPurchaseOrderResults(
  results = [],
  { excludePurchaseOrderNumbers = [], keepPurchaseOrderNumber = '' } = {},
) {
  const keepKey = poSearchKey(keepPurchaseOrderNumber)
  const excluded = new Set(
    (excludePurchaseOrderNumbers || [])
      .map(poSearchKey)
      .filter(key => key && key !== keepKey),
  )
  if (!excluded.size) return [...results]

  return (results || []).filter(
    row => !excluded.has(poSearchKey(row?.purchaseOrderNumber)),
  )
}

/**
 * Rank PO search hits for invoice matching:
 * 1. Not yet sent to Xero (still open to reconcile)
 * 2. Delivery/order date closest to the invoice date
 * 3. Remaining/order value closest to invoice total (when totals are present)
 * 4. Newer orderedDate
 *
 * Venue + supplier filtering stays on the search API (`venueId` + `supplierId`).
 */
export function rankPurchaseOrderSearchResults(
  results = [],
  { invoiceDate, invoiceTotalPennies } = {},
) {
  const invoiceTime = toTime(invoiceDate)
  const invoiceTotal = toPenniesOrNull(invoiceTotalPennies)

  return [...results].sort((left, right) => {
    const leftClosed = Boolean(left?.sentToXero)
    const rightClosed = Boolean(right?.sentToXero)
    if (leftClosed !== rightClosed) return leftClosed ? 1 : -1

    if (invoiceTime != null) {
      const leftDistance = dateDistanceMs(left, invoiceTime)
      const rightDistance = dateDistanceMs(right, invoiceTime)
      const leftScore =
        leftDistance == null ? Number.POSITIVE_INFINITY : leftDistance
      const rightScore =
        rightDistance == null ? Number.POSITIVE_INFINITY : rightDistance
      if (leftScore !== rightScore) return leftScore - rightScore
    }

    if (invoiceTotal != null) {
      const leftValue = valueDistancePennies(left, invoiceTotal)
      const rightValue = valueDistancePennies(right, invoiceTotal)
      const leftScore = leftValue == null ? Number.POSITIVE_INFINITY : leftValue
      const rightScore =
        rightValue == null ? Number.POSITIVE_INFINITY : rightValue
      if (leftScore !== rightScore) return leftScore - rightScore
    }

    const leftOrdered = toTime(left?.orderedDate) || 0
    const rightOrdered = toTime(right?.orderedDate) || 0
    return rightOrdered - leftOrdered
  })
}

/**
 * Apply exclude filter then Phase 5 ranking (safe no-op when options omitted).
 */
export function preparePurchaseOrderSearchResults(
  results = [],
  {
    invoiceDate,
    invoiceTotalPennies,
    excludePurchaseOrderNumbers,
    keepPurchaseOrderNumber,
  } = {},
) {
  const filtered = filterExcludedPurchaseOrderResults(results, {
    excludePurchaseOrderNumbers,
    keepPurchaseOrderNumber,
  })
  return rankPurchaseOrderSearchResults(filtered, {
    invoiceDate,
    invoiceTotalPennies,
  })
}

function formatPoundsFromPennies(pennies) {
  const value = toPenniesOrNull(pennies)
  if (value == null) return ''
  return `£${(value / 100).toFixed(2)}`
}

export function formatPurchaseOrderSearchOptionLabel(
  po = {},
  { showHints = false, invoiceDate } = {},
) {
  const poNumber = String(po?.purchaseOrderNumber || '').trim()
  const supplier = String(po?.supplierTitle || '').trim()
  if (!showHints) {
    return supplier ? `${poNumber} — ${supplier}` : poNumber
  }

  const date = formatSearchDate(po.deliveryDate || po.orderedDate)
  const status = po.sentToXero
    ? 'Sent to Xero'
    : String(po.status || '').trim() || 'Open'
  const parts = [poNumber, date, status]

  const invoiceTime = toTime(invoiceDate)
  const distance = dateDistanceMs(po, invoiceTime)
  if (distance != null && distance <= NEAR_INVOICE_DATE_MS && !po.sentToXero) {
    parts.push('Near invoice date')
  }

  const amountLabel = formatPoundsFromPennies(
    po.remainingBalancePennies ?? po.orderGrossPennies ?? po.grossPennies,
  )
  if (amountLabel) {
    parts.push(amountLabel)
  }

  if (supplier) parts.push(supplier)
  return parts.filter(Boolean).join(' — ')
}
